import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './OrganizationCreatePage.css';
import { createOrganizationApi } from '../services/apiService';
import { logout } from '../services/authService';

export default function OrganizationCreatePage() {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [organisationId, setOrganisationId] = useState('');
  const [hubIds, setHubIds] = useState(['']);
  const [primaryHubIndex, setPrimaryHubIndex] = useState(0);
  const [description, setDescription] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [address, setAddress] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [created, setCreated] = useState(null);

  const setHubIdAt = (idx, value) => {
    setHubIds((prev) => prev.map((v, i) => (i === idx ? value : v)));
  };

  const addHubRow = () => {
    setHubIds((prev) => [...prev, '']);
  };

  const removeHubRow = (idx) => {
    setHubIds((prev) => {
      if (prev.length <= 1) return prev;
      const next = prev.filter((_, i) => i !== idx);
      return next.length ? next : [''];
    });
    setPrimaryHubIndex((prev) => {
      if (idx === prev) return 0;
      if (idx < prev) return prev - 1;
      return prev;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setCreated(null);

    const trimmed = name.trim();
    if (!trimmed) {
      setError('Please enter organisation name.');
      return;
    }

    const oidStr = organisationId.trim();
    if (!oidStr) {
      setError('Organisation Id is required.');
      return;
    }
    const oid = parseInt(oidStr, 10);
    if (isNaN(oid) || oid < 0) {
      setError('Organisation Id must be a valid number.');
      return;
    }

    const parsedHubIds = hubIds
      .map((s) => (s ?? '').toString().trim())
      .filter((s) => s.length > 0)
      .map((s) => Number(s))
      .filter((n) => Number.isFinite(n) && n > 0);

    const uniqueHubIds = Array.from(new Set(parsedHubIds));
    if (uniqueHubIds.length === 0) {
      setError('At least one Hub ID is required.');
      return;
    }

    const primaryCandidate = (hubIds[primaryHubIndex] ?? '').toString().trim();
    const primaryHubId = primaryCandidate ? Number(primaryCandidate) : uniqueHubIds[0];
    if (!Number.isFinite(primaryHubId) || primaryHubId <= 0) {
      setError('Primary Hub ID must be a valid number.');
      return;
    }

    const payload = { name: trimmed, organisationId: oid, hubIds: uniqueHubIds, hubId: primaryHubId };

    const desc = description.trim();
    if (desc) payload.description = desc;

    const ce = contactEmail.trim();
    if (ce) payload.contactEmail = ce;

    const addr = address.trim();
    if (addr) payload.address = addr;

    const cp = contactPhone.trim();
    if (cp) payload.contactPhone = cp;

    setSubmitting(true);
    try {
      const org = await createOrganizationApi(payload);
      setCreated(org);
      setName('');
      setOrganisationId('');
      setHubIds(['']);
      setPrimaryHubIndex(0);
      setDescription('');
      setContactEmail('');
      setAddress('');
      setContactPhone('');
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Failed to create organisation.';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login', { replace: true });
  };

  return (
    <div className="org-create-page">
      <header className="org-create-topbar">
        <h1 className="org-create-title">Create Organisation</h1>
        <div className="org-create-actions">
          <button type="button" className="org-btn-secondary" onClick={() => navigate('/admin/users-and-organisations')}>
            Users & Organisations
          </button>
          <button type="button" className="org-btn-secondary" onClick={() => navigate('/settings')}>
            Settings
          </button>
          <button type="button" className="org-btn-logout" onClick={handleLogout}>
            Logout
          </button>
        </div>
      </header>

      <div className="org-create-content">
        <div className="org-create-card">
          <form onSubmit={handleSubmit} className="org-create-form">
          {error && <div className="org-alert org-alert-error">{error}</div>}
          {created && (
            <div className="org-alert org-alert-success">
              Created: <strong>{created.name}</strong> — Organisation ID: <strong>{created.id}</strong>
              {created.organisationId != null ? (
                <> — Organisation Id: <strong>{created.organisationId}</strong></>
              ) : null}
              {created.hubId != null ? (
                <> — Hub ID: <strong>{created.hubId}</strong></>
              ) : null}
              {created.description ? (
                <> — Description: <strong>{created.description}</strong></>
              ) : null}
              {created.contactEmail ? (
                <> — Contact Email: <strong>{created.contactEmail}</strong></>
              ) : null}
              {created.address ? (
                <> — Address: <strong>{created.address}</strong></>
              ) : null}
              {created.contactPhone ? (
                <> — Contact Phone: <strong>{created.contactPhone}</strong></>
              ) : null}
            </div>
          )}

          <div className="org-field">
            <label htmlFor="org-name">Organisation Name</label>
            <input
              id="org-name"
              className="org-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Organisation name"
              autoComplete="organization"
            />
          </div>

          <div className="org-field">
            <label htmlFor="org-organisation-id">Organisation Id <span className="org-required">*</span></label>
            <input
              id="org-organisation-id"
              className="org-input"
              type="number"
              min="0"
              value={organisationId}
              onChange={(e) => setOrganisationId(e.target.value)}
              placeholder="Required"
              required
            />
          </div>

          <div className="org-field">
            <label>Hub IDs <span className="org-required">*</span></label>
            <div className="org-hub-list">
              {hubIds.map((v, idx) => (
                <div key={idx} className="org-hub-row">
                  <label className="org-hub-primary">
                    <input
                      type="radio"
                      name="primaryHub"
                      checked={primaryHubIndex === idx}
                      onChange={() => setPrimaryHubIndex(idx)}
                    />
                    Primary
                  </label>
                  <input
                    className="org-input"
                    inputMode="numeric"
                    value={v}
                    onChange={(e) => setHubIdAt(idx, e.target.value)}
                    placeholder="Enter Hub ID"
                  />
                  <button
                    type="button"
                    className="org-btn-secondary"
                    onClick={() => removeHubRow(idx)}
                    disabled={hubIds.length <= 1}
                  >
                    Remove
                  </button>
                </div>
              ))}
              <button type="button" className="org-btn-secondary" onClick={addHubRow}>
                Add another Hub ID
              </button>
            </div>
          </div>

          <div className="org-field">
            <label htmlFor="org-description">Description</label>
            <textarea
              id="org-description"
              className="org-input"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optional"
              rows={3}
            />
          </div>

          <div className="org-field">
            <label htmlFor="org-contact-email">Contact Email</label>
            <input
              id="org-contact-email"
              className="org-input"
              type="email"
              value={contactEmail}
              onChange={(e) => setContactEmail(e.target.value)}
              placeholder="Optional"
              autoComplete="email"
            />
          </div>

          <div className="org-field">
            <label htmlFor="org-contact-phone">Contact Phone</label>
            <input
              id="org-contact-phone"
              className="org-input"
              value={contactPhone}
              onChange={(e) => setContactPhone(e.target.value)}
              placeholder="Optional"
              autoComplete="tel"
            />
          </div>

          <div className="org-field">
            <label htmlFor="org-address">Address</label>
            <textarea
              id="org-address"
              className="org-input"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              placeholder="Optional"
              rows={3}
            />
          </div>

          <button type="submit" className="org-btn-primary org-btn-submit" disabled={submitting}>
            {submitting ? 'Creating...' : 'Create Organisation'}
          </button>
        </form>
        </div>
      </div>
    </div>
  );
}

